import type { DocLocator, FixAction, Issue } from '@qcbot/shared';
import { FixDriftError } from './fixErrors';

/**
 * Applies/undoes fixes in the open Word document.
 *
 * Word has no stable object ids, so a fix is re-located the way QC saw the text: by paragraph
 * index (the same order as `body.paragraphs`) AND by content. Content is the gate — a fix only
 * applies when the exact text it expects is still present exactly once in the paragraph — so an
 * edit that shifted paragraph numbers is still found by what it contains, and a changed paragraph
 * is reported as drift rather than silently edited. Only the characters that change are rewritten,
 * so the surrounding runs keep their formatting.
 */

export type WordFixSnapshot =
  /** `before` was rewritten to `after` in the paragraph; undo rewrites `after` back to `before`. */
  | { kind: 'wordEdit'; locator: DocLocator; before: string; after: string }
  | { kind: 'wordFontSize'; locator: DocLocator; anchorText: string; rangeOffset: number; rangeLength: number; oldSizePt: number | null }
  | { kind: 'wordCells'; locator: DocLocator; entries: { rowIndex: number; cellIndex: number; oldText: string; newText: string }[] }
  | { kind: 'wordFont'; entries: { locator: DocLocator; oldTypeface: string | null }[] }
  | { kind: 'wordInserted'; locator: DocLocator; text: string }
  /** Steps applied in order; undo reverses them in reverse order. `skipped` lists steps that couldn't be applied. */
  | { kind: 'wordComposite'; snapshots: WordFixSnapshot[]; skipped: { slideNumber: number; reason: string }[] };

/** Word's own search limit for a single find string. */
const MAX_SEARCH_CHARS = 255;
const SEARCH_OPTIONS = { matchCase: true, matchWildcards: false } as const;

export async function applyWordFix(issue: Issue): Promise<WordFixSnapshot> {
  const action = issue.fixAction;
  if (!action) throw new Error('No fix is available for this issue.');
  return applyAction(action);
}

async function applyAction(action: FixAction): Promise<WordFixSnapshot> {
  switch (action.type) {
    case 'replaceText':
      return applyReplaceText(action);
    case 'setCellText':
      return applySetCellText(action);
    case 'insertTextbox':
      return applyInsertParagraph(action);
    case 'setFontSize':
      return applySetFontSize(action);
    case 'setFontTypeface':
      return applySetFontTypeface(action);
    case 'composite':
      return applyComposite(action);
    default:
      throw new Error(`Unsupported fix type: ${action.type as string}`);
  }
}

export async function undoWordFix(snapshot: WordFixSnapshot): Promise<void> {
  switch (snapshot.kind) {
    case 'wordEdit':
      return undoReplaceText(snapshot);
    case 'wordFontSize':
      return undoSetFontSize(snapshot);
    case 'wordCells':
      return undoSetCellText(snapshot);
    case 'wordFont':
      return undoSetFontTypeface(snapshot);
    case 'wordInserted':
      return undoInsertParagraph(snapshot);
    case 'wordComposite':
      for (const inner of [...snapshot.snapshots].reverse()) await undoWordFix(inner);
  }
}

// ---------------------------------------------------------------------------------------------
// Text helpers
// ---------------------------------------------------------------------------------------------

/** Word reports a soft line break as "\v"; the backend's parsed text uses "\n" for it. */
function normalizeText(text: string): string {
  return text.replace(/[\r\n]+$/, '').replace(/\v/g, '\n');
}

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  for (let i = haystack.indexOf(needle); i !== -1; i = haystack.indexOf(needle, i + needle.length)) count += 1;
  return count;
}

/** Positions of `needle` in `haystack`, counted the way Word's (non-overlapping) search finds them. */
function occurrenceStarts(haystack: string, needle: string): number[] {
  const starts: number[] = [];
  if (!needle) return starts;
  for (let i = haystack.indexOf(needle); i !== -1; i = haystack.indexOf(needle, i + needle.length)) starts.push(i);
  return starts;
}

/** Word's Find treats "^" as the start of a special code; tabs and manual line breaks have their own codes. */
function toSearchPattern(text: string): string {
  if (text.length > MAX_SEARCH_CHARS) {
    throw new Error(`This fix needs to find ${text.length} characters, more than Word's ${MAX_SEARCH_CHARS}-character search limit. Please fix it by hand.`);
  }
  return text.replace(/\^/g, '^^').replace(/\t/g, '^t').replace(/\n/g, '^l');
}

function diagnoseTextMismatch(haystack: string, needle: string): string {
  const count = countOccurrences(haystack, needle);
  if (count > 1) return `"${needle}" appears ${count} times in this paragraph, so it's ambiguous which one to change`;
  if (countOccurrences(haystack.toLowerCase(), needle.toLowerCase()) > 0) return `text matching "${needle}" was found but with different capitalization`;
  if (countOccurrences(collapse(haystack), collapse(needle)) > 0) return `text matching "${needle}" was found but with different spacing/line breaks`;
  return `the exact text "${needle}" is no longer present in the document`;
}

// ---------------------------------------------------------------------------------------------
// Paragraph / table resolution
// ---------------------------------------------------------------------------------------------

interface LiveParagraphs {
  items: Word.Paragraph[];
  texts: string[];
}

async function loadParagraphs(context: Word.RequestContext): Promise<LiveParagraphs> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load('items/text');
  await context.sync();
  return { items: paragraphs.items, texts: paragraphs.items.map((p) => normalizeText(p.text)) };
}

function hintMatches(text: string, locator: DocLocator | undefined): boolean {
  return !locator?.textHint || collapse(text).startsWith(locator.textHint);
}

/**
 * The paragraph a fix means: the one at the recorded index if its content passes `accept`,
 * otherwise the single paragraph anywhere in the document that does (ties broken by the text hint).
 * Returns undefined when nothing qualifies and throws when several still do.
 */
function locateParagraph(live: LiveParagraphs, locator: DocLocator | undefined, accept: (text: string) => boolean, what: string): number | undefined {
  const byIndex = locator?.paragraphIndex;
  if (byIndex !== undefined && live.texts[byIndex] !== undefined && accept(live.texts[byIndex])) return byIndex;

  const matches = live.texts.flatMap((text, i) => (accept(text) ? [i] : []));
  if (matches.length <= 1) return matches[0];
  const hinted = matches.filter((i) => hintMatches(live.texts[i], locator));
  if (hinted.length === 1) return hinted[0];
  throw new FixDriftError(`Couldn't apply: ${what} appears in ${matches.length} places, so it's ambiguous which one to change. Please fix by hand.`);
}

function requireParagraph(live: LiveParagraphs, locator: DocLocator | undefined, findText: string): number {
  const index = locateParagraph(live, locator, (text) => countOccurrences(text, findText) === 1, `"${findText}"`);
  if (index !== undefined) return index;
  const intended = locator?.paragraphIndex !== undefined ? live.texts[locator.paragraphIndex] : undefined;
  const reason = intended !== undefined ? diagnoseTextMismatch(intended, findText) : `the text "${findText}" was not found in the document`;
  throw new FixDriftError(`Couldn't apply: ${reason}. Please re-run QC to refresh this fix.`);
}

/** The single range matching `text` inside `scope`, or a drift error. */
async function findOne(context: Word.RequestContext, scope: Word.Paragraph | Word.Range | Word.Body, text: string): Promise<Word.Range> {
  const found = scope.search(toSearchPattern(text), SEARCH_OPTIONS);
  found.load('items');
  await context.sync();
  if (found.items.length !== 1) {
    throw new FixDriftError(`Couldn't apply: "${text}" could not be located exactly once in the document. Please re-run QC to refresh this fix.`);
  }
  return found.items[0];
}

/** The range for `inner`, which sits at `offset` inside `whole` (a range that matched `whole` exactly). */
async function findAtOffset(context: Word.RequestContext, whole: Word.Range, wholeText: string, inner: string, offset: number): Promise<Word.Range> {
  const starts = occurrenceStarts(wholeText, inner);
  const k = starts.indexOf(offset);
  const found = whole.search(toSearchPattern(inner), SEARCH_OPTIONS);
  found.load('items');
  await context.sync();
  if (k < 0 || found.items.length !== starts.length) {
    throw new FixDriftError(`Couldn't apply: couldn't pin down "${inner}" inside the paragraph. Please re-run QC to refresh this fix.`);
  }
  return found.items[k];
}

function commonPrefixLength(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return i;
}

function commonSuffixLength(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[a.length - 1 - i] === b[b.length - 1 - i]) i += 1;
  return i;
}

/** Rewrites `before` (found exactly once in `paragraph`) to `after`, touching only the changed characters. */
async function editParagraphText(context: Word.RequestContext, paragraph: Word.Paragraph, before: string, after: string): Promise<void> {
  const prefix = commonPrefixLength(before, after);
  const suffix = commonSuffixLength(before.slice(prefix), after.slice(prefix));
  const oldSpan = before.slice(prefix, before.length - suffix);
  const newSpan = after.slice(prefix, after.length - suffix);

  const whole = await findOne(context, paragraph, before);
  if (oldSpan === '') {
    if (prefix === 0) {
      whole.insertText(newSpan, 'Start');
    } else {
      const head = await findAtOffset(context, whole, before, before.slice(0, prefix), 0);
      head.insertText(newSpan, 'After');
    }
  } else {
    const target = await findAtOffset(context, whole, before, oldSpan, prefix);
    if (newSpan === '') target.delete();
    else target.insertText(newSpan, 'Replace');
  }
  await context.sync();
}

// ---------------------------------------------------------------------------------------------
// replaceText
// ---------------------------------------------------------------------------------------------

async function applyReplaceText(action: FixAction): Promise<WordFixSnapshot> {
  if (!action.findText || action.replaceWith === undefined) throw new Error('Incomplete fix action.');
  const { findText, replaceWith } = action;
  let snapshot!: WordFixSnapshot;
  await Word.run(async (context) => {
    const live = await loadParagraphs(context);
    const index = requireParagraph(live, action.locator, findText);
    await editParagraphText(context, live.items[index], findText, replaceWith);
    snapshot = { kind: 'wordEdit', locator: { ...action.locator, paragraphIndex: index }, before: findText, after: replaceWith };
  });
  return snapshot;
}

async function undoReplaceText(snapshot: Extract<WordFixSnapshot, { kind: 'wordEdit' }>): Promise<void> {
  if (!snapshot.after) throw new Error("This fix can't be undone automatically.");
  await Word.run(async (context) => {
    const live = await loadParagraphs(context);
    const index = locateParagraph(live, snapshot.locator, (text) => countOccurrences(text, snapshot.after) === 1, `"${snapshot.after}"`);
    if (index === undefined) throw new FixDriftError("Couldn't undo: this text has been edited since the fix was applied.");
    await editParagraphText(context, live.items[index], snapshot.after, snapshot.before);
  });
}

// ---------------------------------------------------------------------------------------------
// setFontSize
// ---------------------------------------------------------------------------------------------

async function locateStyledRange(
  context: Word.RequestContext,
  locator: DocLocator | undefined,
  anchorText: string,
  rangeOffset: number,
  rangeLength: number,
): Promise<{ range: Word.Range; index: number }> {
  const live = await loadParagraphs(context);
  const index = requireParagraph(live, locator, anchorText);
  const anchor = await findOne(context, live.items[index], anchorText);
  const range = await findAtOffset(context, anchor, anchorText, anchorText.slice(rangeOffset, rangeOffset + rangeLength), rangeOffset);
  return { range, index };
}

async function applySetFontSize(action: FixAction): Promise<WordFixSnapshot> {
  if (!action.findText || action.rangeOffset === undefined || !action.rangeLength || !action.fontSizePt) throw new Error('Incomplete fix action.');
  const { findText, rangeOffset, rangeLength, fontSizePt } = action;
  let snapshot!: WordFixSnapshot;
  await Word.run(async (context) => {
    const { range, index } = await locateStyledRange(context, action.locator, findText, rangeOffset, rangeLength);
    range.font.load('size');
    await context.sync();
    // 0 means the span already mixed sizes — there's no single size to restore on undo.
    const oldSizePt = range.font.size > 0 ? range.font.size : null;
    range.font.size = fontSizePt;
    await context.sync();
    snapshot = { kind: 'wordFontSize', locator: { ...action.locator, paragraphIndex: index }, anchorText: findText, rangeOffset, rangeLength, oldSizePt };
  });
  return snapshot;
}

async function undoSetFontSize(snapshot: Extract<WordFixSnapshot, { kind: 'wordFontSize' }>): Promise<void> {
  if (snapshot.oldSizePt === null) return;
  await Word.run(async (context) => {
    const { range } = await locateStyledRange(context, snapshot.locator, snapshot.anchorText, snapshot.rangeOffset, snapshot.rangeLength);
    range.font.size = snapshot.oldSizePt!;
    await context.sync();
  });
}

// ---------------------------------------------------------------------------------------------
// setFontTypeface
// ---------------------------------------------------------------------------------------------

/** A non-empty paragraph that still starts with the locator's text: at the recorded index, else the only one anywhere. */
function resolveByHint(live: LiveParagraphs, locator: DocLocator): number | undefined {
  const at = locator.paragraphIndex;
  if (at !== undefined && live.texts[at]?.trim() && hintMatches(live.texts[at], locator)) return at;
  if (!locator.textHint) return undefined;
  const found = live.texts.flatMap((text, i) => (text.trim() && hintMatches(text, locator) ? [i] : []));
  return found.length === 1 ? found[0] : undefined;
}

async function applySetFontTypeface(action: FixAction): Promise<WordFixSnapshot> {
  const wanted = action.locators ?? [];
  if (!action.fontTypeface || wanted.length === 0) throw new Error('Incomplete fix action.');
  const entries: { locator: DocLocator; oldTypeface: string | null }[] = [];
  await Word.run(async (context) => {
    const live = await loadParagraphs(context);
    const targets: { locator: DocLocator; paragraph: Word.Paragraph }[] = [];
    for (const locator of wanted) {
      // No content to gate on here, so the paragraph must still start with the text QC saw.
      const resolved = resolveByHint(live, locator);
      if (resolved !== undefined) targets.push({ locator: { ...locator, paragraphIndex: resolved }, paragraph: live.items[resolved] });
    }
    if (targets.length === 0) throw new FixDriftError('None of the flagged paragraphs were found — the document may have changed. Please re-run QC.');

    targets.forEach(({ paragraph }) => paragraph.font.load('name'));
    await context.sync();
    targets.forEach(({ locator, paragraph }) => {
      // An empty name means the paragraph already mixed fonts — undo can't restore a per-run mix.
      entries.push({ locator, oldTypeface: paragraph.font.name || null });
      paragraph.font.name = action.fontTypeface!;
    });
    await context.sync();
  });
  return { kind: 'wordFont', entries };
}

async function undoSetFontTypeface(snapshot: Extract<WordFixSnapshot, { kind: 'wordFont' }>): Promise<void> {
  await Word.run(async (context) => {
    const live = await loadParagraphs(context);
    for (const entry of snapshot.entries) {
      const index = entry.locator.paragraphIndex;
      if (!entry.oldTypeface || index === undefined || live.items[index] === undefined || !hintMatches(live.texts[index], entry.locator)) continue;
      live.items[index].font.name = entry.oldTypeface;
    }
    await context.sync();
  });
}

// ---------------------------------------------------------------------------------------------
// setCellText
// ---------------------------------------------------------------------------------------------

interface LiveTable {
  table: Word.Table;
  values: string[][];
}

async function loadTables(context: Word.RequestContext): Promise<LiveTable[]> {
  const tables = context.document.body.tables;
  tables.load('items/values');
  await context.sync();
  return tables.items.map((table) => ({ table, values: table.values }));
}

function cellText(values: string[][], rowIndex: number, cellIndex: number): string | undefined {
  const raw = values[rowIndex]?.[cellIndex];
  return raw === undefined ? undefined : collapse(normalizeText(raw));
}

/** The one table whose cells read exactly `expected` — the recorded index first, then any other. */
function locateTable(tables: LiveTable[], locator: DocLocator | undefined, edits: { rowIndex: number; cellIndex: number }[], expected: string[]): LiveTable {
  const matches = (t: LiveTable) => edits.every((e, i) => cellText(t.values, e.rowIndex, e.cellIndex) === collapse(expected[i]));
  const byIndex = locator?.tableIndex !== undefined ? tables[locator.tableIndex] : undefined;
  if (byIndex && matches(byIndex)) return byIndex;
  const all = tables.filter(matches);
  if (all.length === 1) return all[0];
  if (all.length > 1) throw new FixDriftError("Couldn't apply: the expected table cells appear in more than one table, so it's ambiguous which one to change. Please fix by hand.");
  const intended = byIndex ?? tables[0];
  const bad = intended ? edits.findIndex((e, i) => cellText(intended.values, e.rowIndex, e.cellIndex) !== collapse(expected[i])) : -1;
  const reason =
    intended && bad >= 0
      ? `expected cell (row ${edits[bad].rowIndex + 1}, col ${edits[bad].cellIndex + 1}) to read "${expected[bad]}" but found "${cellText(intended.values, edits[bad].rowIndex, edits[bad].cellIndex) ?? '(cell no longer exists)'}"`
      : 'no table in the document has the expected cell values';
  throw new FixDriftError(`Couldn't apply: ${reason}. Please re-run QC to refresh this fix.`);
}

async function writeCell(context: Word.RequestContext, table: Word.Table, rowIndex: number, cellIndex: number, from: string, to: string): Promise<void> {
  const cell = table.getCell(rowIndex, cellIndex);
  const found = cell.body.search(toSearchPattern(from.trim()), SEARCH_OPTIONS);
  found.load('items');
  await context.sync();
  // Replacing just the matched text keeps the cell's own formatting; a value rewrite is the fallback.
  if (found.items.length === 1) found.items[0].insertText(to, 'Replace');
  else cell.value = to;
  await context.sync();
}

async function applySetCellText(action: FixAction): Promise<WordFixSnapshot> {
  if (!action.cells || action.cells.length === 0) throw new Error('Incomplete fix action.');
  const edits = action.cells;
  let snapshot!: WordFixSnapshot;
  await Word.run(async (context) => {
    const tables = await loadTables(context);
    // Every cell must still read exactly what QC saw before ANY is changed — a multi-cell fix
    // applies as one all-or-nothing unit.
    const live = locateTable(tables, action.locator, edits, edits.map((e) => e.findText));
    for (const edit of edits) await writeCell(context, live.table, edit.rowIndex, edit.cellIndex, edit.findText, edit.replaceWith);
    snapshot = {
      kind: 'wordCells',
      locator: { ...action.locator, tableIndex: tables.indexOf(live) },
      entries: edits.map((e) => ({ rowIndex: e.rowIndex, cellIndex: e.cellIndex, oldText: e.findText.trim(), newText: e.replaceWith })),
    };
  });
  return snapshot;
}

async function undoSetCellText(snapshot: Extract<WordFixSnapshot, { kind: 'wordCells' }>): Promise<void> {
  await Word.run(async (context) => {
    const tables = await loadTables(context);
    let live: LiveTable;
    try {
      live = locateTable(tables, snapshot.locator, snapshot.entries, snapshot.entries.map((e) => e.newText));
    } catch {
      throw new FixDriftError("Couldn't undo: these table cells have been edited since the fix was applied.");
    }
    for (const entry of snapshot.entries) await writeCell(context, live.table, entry.rowIndex, entry.cellIndex, entry.newText, entry.oldText);
  });
}

// ---------------------------------------------------------------------------------------------
// insertTextbox (a new paragraph after the flagged block)
// ---------------------------------------------------------------------------------------------

async function applyInsertParagraph(action: FixAction): Promise<WordFixSnapshot> {
  if (!action.insertText) throw new Error('Incomplete fix action.');
  const insertText = action.insertText;
  const locator = action.locator;
  if (!locator) throw new Error("This fix has no position in the document. Please re-run QC.");
  let snapshot!: WordFixSnapshot;
  await Word.run(async (context) => {
    let inserted: Word.Paragraph;
    if (locator.tableIndex !== undefined) {
      const tables = await loadTables(context);
      const live = tables[locator.tableIndex];
      if (!live) throw new FixDriftError("Couldn't apply: the table this fix refers to no longer exists. Please re-run QC.");
      inserted = live.table.insertParagraph(insertText, 'After');
    } else {
      const live = await loadParagraphs(context);
      const index = locator.paragraphIndex;
      if (index === undefined || live.items[index] === undefined || !hintMatches(live.texts[index], locator)) {
        throw new FixDriftError("Couldn't apply: the paragraph this fix refers to has changed. Please re-run QC.");
      }
      inserted = live.items[index].insertParagraph(insertText, 'After');
    }
    // The new paragraph inherits the flagged block's style (e.g. a heading) — footnote text should be plain.
    inserted.styleBuiltIn = 'Normal' as Word.BuiltInStyleName;
    await context.sync();
    snapshot = { kind: 'wordInserted', locator, text: insertText };
  });
  return snapshot;
}

async function undoInsertParagraph(snapshot: Extract<WordFixSnapshot, { kind: 'wordInserted' }>): Promise<void> {
  await Word.run(async (context) => {
    const live = await loadParagraphs(context);
    const wanted = collapse(snapshot.text);
    const matches = live.texts.flatMap((text, i) => (collapse(text) === wanted ? [i] : []));
    const expected = snapshot.locator.tableIndex === undefined && snapshot.locator.paragraphIndex !== undefined ? snapshot.locator.paragraphIndex + 1 : undefined;
    const index = expected !== undefined && matches.includes(expected) ? expected : matches.length === 1 ? matches[0] : undefined;
    if (index === undefined) throw new FixDriftError("Couldn't undo: the inserted paragraph was edited or moved since the fix was applied.");
    live.items[index].delete();
    await context.sync();
  });
}

// ---------------------------------------------------------------------------------------------
// composite
// ---------------------------------------------------------------------------------------------

/** Applies every step it can, in order. One stale step no longer blocks the rest: failed steps are
 * reported in `skipped` for the reviewer to handle by hand, and Undo reverses exactly the steps
 * applied. Only when NO step applies does this throw. */
async function applyComposite(action: FixAction): Promise<WordFixSnapshot> {
  const steps = action.steps ?? [];
  if (steps.length === 0) throw new Error('Incomplete fix action.');
  const applied: WordFixSnapshot[] = [];
  const skipped: { slideNumber: number; reason: string }[] = [];
  let firstError: unknown;
  for (const step of steps) {
    try {
      applied.push(await applyAction(step.action));
    } catch (error) {
      firstError ??= error;
      skipped.push({ slideNumber: step.slideNumber, reason: error instanceof Error ? error.message : String(error) });
    }
  }
  if (applied.length === 0) {
    const message = skipped[0].reason;
    throw firstError instanceof FixDriftError ? new FixDriftError(message) : new Error(message);
  }
  return { kind: 'wordComposite', snapshots: applied, skipped };
}

// ---------------------------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------------------------

/** Scrolls to and selects the paragraph an issue points at (falling back to the recorded index
 * when the text hint no longer matches, so a click always lands somewhere sensible). */
export async function navigateToWordLocation(locator: DocLocator | undefined): Promise<void> {
  if (locator?.paragraphIndex === undefined) return;
  await Word.run(async (context) => {
    const live = await loadParagraphs(context);
    let index = locator.paragraphIndex!;
    if (live.texts[index] === undefined || !hintMatches(live.texts[index], locator)) {
      const hinted = live.texts.flatMap((text, i) => (hintMatches(text, locator) && text.trim() !== '' ? [i] : []));
      if (hinted.length === 1) index = hinted[0];
    }
    const paragraph = live.items[index] ?? live.items[live.items.length - 1];
    paragraph?.select();
    await context.sync();
  });
}
